import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { RmButton, RmEditorPage, RmFooter } from '../RemindersEditor/EditorShell';
import type { GapRule, GapWindow } from '../../settings-gapfiller/types';

export interface GapRuleViewProps {
  rule: GapRule;
  /** New rules and custom rules can be deleted; built-in ones cannot. */
  isNew: boolean;
  /** Which field to focus, from `?focus=` (card 05's *Shorten a rule's minimum*). */
  focus?: string | undefined;
  onSave: (rule: GapRule) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const DAYS: GapWindow['days'] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Add or edit a rule (amendment 26 §5, board `GF1Rule`).
 *
 * The left column is **the recall audience box** (spec 21 §2): a description that
 * ELVA reads back, the standing exclusions, a live count and the first rows with
 * *Leave out*. A built-in rule's patients are defined by ELVA, so its description
 * is read-only; its name, gaps and days/times are still editable. The right
 * column is *which gaps* these patients can be booked into.
 */
export function GapRuleView({ rule, isNew, focus, onSave, onCancel, onDelete }: GapRuleViewProps) {
  const [draft, setDraft] = useState<GapRule>(rule);
  const [built, setBuilt] = useState(rule.description.trim() !== '' && !isNew);
  const set = (patch: Partial<GapRule>) => { setDraft({ ...draft, ...patch }); };
  const readOnlyDescription = !draft.custom;

  return (
    <RmEditorPage>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Settings › Gap Filler › {isNew ? 'Add a rule' : draft.name || 'Rule'}
      </div>
      <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
        Who should be offered a <span className="text-[var(--brand-indigo)]">gap</span>?
      </h1>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        Describe the patients, then say which gaps they can be booked into.
      </p>

      {/* rule name */}
      <div className="mt-5 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
        <FieldLabel>Rule name</FieldLabel>
        <input
          value={draft.name}
          onChange={(e) => { set({ name: e.target.value }); }}
          placeholder="Kids after school"
          className="mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[14px] font-semibold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
        />
        <div className="mt-1 text-[11px] text-[var(--text-muted)]">Shown on the settings list and on each gap.</div>
      </div>

      <div className="mt-4 flex items-start gap-5 max-[1100px]:flex-col">
        {/* left — which patients (the audience box) */}
        <div className="min-w-0 flex-1 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Which patients</div>
          <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">
            {readOnlyDescription
              ? 'Defined by ELVA for this built-in rule.'
              : 'Describe them in your own words. ELVA reads it back so you can check.'}
          </p>
          <textarea
            value={draft.description}
            readOnly={readOnlyDescription}
            onChange={(e) => { set({ description: e.target.value }); setBuilt(false); }}
            rows={2}
            className={cn(
              'mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[13.5px] leading-[1.6] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]',
              readOnlyDescription && 'bg-[var(--surface-sunken)]',
            )}
          />
          {!readOnlyDescription ? (
            <button
              type="button"
              onClick={() => { setBuilt(true); }}
              className="mt-2 rounded-[9px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12.5px] font-semibold text-white"
            >
              Build the list
            </button>
          ) : null}

          {built || readOnlyDescription ? (
            <div className="mt-3">
              <div className="rounded-[10px] bg-[var(--surface-sunken)] p-3">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                  ELVA understood this as
                </div>
                <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0 text-[12px] text-[var(--text-secondary)]">
                  <li>Younger than 18</li>
                  <li>Due for fluoride or sealants, according to the practice’s records</li>
                  <li>No appointment already booked</li>
                </ul>
                <div className="mt-2 text-[11px] text-[var(--text-muted)]">
                  Always left out: anyone who opted out of these messages, has no mobile or email, or has had enough
                  messages this fortnight.
                </div>
              </div>
              <div className="mt-2 text-[12.5px] text-[var(--text-secondary)]">
                <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">46</b> patients →{' '}
                <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">41</b> can be offered a gap today
              </div>
              <div className="mt-2 flex flex-col">
                {SAMPLE.map((s, i) => (
                  <div
                    key={s.name}
                    className="flex items-center gap-3 py-2 text-[12px]"
                    style={i === SAMPLE.length - 1 ? undefined : { borderBottom: '1px solid #edf0f6' }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-[var(--text-primary)]">{s.name}</span>
                      <span className="block text-[11px] text-[var(--text-muted)]">{s.meta} · {s.why}</span>
                    </span>
                    <span className="shrink-0 text-[11px]" style={{ color: s.blocked ? 'var(--warn-amber)' : 'var(--money-green)' }}>
                      {s.blocked ? '● Enough messages this fortnight' : '● Can be offered'}
                    </span>
                    <button type="button" className="shrink-0 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                      Leave out
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="mt-1.5 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
                See all 46
              </button>
            </div>
          ) : null}
        </div>

        {/* right — which gaps */}
        <div className="w-[360px] shrink-0 max-[1100px]:w-full">
          <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Which gaps</div>
            <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">The free time these patients can be booked into.</p>

            <div className="mt-3">
              <FieldLabel>Book them into</FieldLabel>
              <input
                value={draft.bookInto}
                onChange={(e) => { set({ bookInto: e.target.value }); }}
                className="mt-1.5 w-full rounded-[9px] border border-[var(--border-default)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
              />
              <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                Or <i>What they’re due for</i>, or <i>Their accepted treatment</i>.
              </div>
            </div>

            <div className="mt-3">
              <FieldLabel>With</FieldLabel>
              <input
                value={draft.providers}
                onChange={(e) => { set({ providers: e.target.value }); }}
                className="mt-1.5 w-full rounded-[9px] border border-[var(--border-default)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
              />
            </div>

            <div className="mt-3">
              <FieldLabel>Shortest gap</FieldLabel>
              <span className="mt-1.5 flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  autoFocus={focus === 'shortest-gap'}
                  value={draft.shortestGapMinutes}
                  onChange={(e) => { set({ shortestGapMinutes: Math.max(0, Number(e.target.value)) }); }}
                  className={cn(
                    'h-8 w-[64px] rounded-[9px] border px-2 text-center font-[var(--font-mono)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]',
                    focus === 'shortest-gap' ? 'border-[var(--brand-indigo)]' : 'border-[var(--border-default)]',
                  )}
                />
                <span className="text-[12.5px] text-[var(--text-muted)]">minutes</span>
              </span>
            </div>

            <div className="mt-3 border-t border-dashed border-[var(--border-default)] pt-3">
              <label className="flex items-center justify-between gap-3">
                <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">Only for gaps on certain days and times</span>
                <input
                  type="checkbox"
                  checked={draft.window !== undefined}
                  onChange={(e) => {
                    if (e.target.checked) set({ window: draft.window ?? { days: [...DAYS.slice(0, 5)] } });
                    else setDraft(({ window: _w, ...rest }) => rest);
                  }}
                />
              </label>
              {draft.window !== undefined ? (
                <div className="mt-2">
                  <div className="flex flex-wrap gap-1">
                    {DAYS.map((day) => {
                      const on = draft.window?.days.includes(day) ?? false;
                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => {
                            const w = draft.window ?? { days: [] };
                            const days = on ? w.days.filter((x) => x !== day) : [...w.days, day];
                            set({ window: { ...w, days } });
                          }}
                          className={cn(
                            'rounded-[7px] px-2 py-1 text-[11.5px] font-semibold',
                            on ? 'bg-[var(--brand-indigo)] text-white' : 'border border-[var(--border-default)] text-[var(--text-secondary)]',
                          )}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 text-[12px]">
                    <span className="text-[var(--text-muted)]">After</span>
                    <input
                      value={draft.window.afterTime ?? ''}
                      placeholder="3:00 pm"
                      onChange={(e) => { const w = draft.window; if (w !== undefined) set({ window: { ...w, afterTime: e.target.value } }); }}
                      className="h-7 w-[90px] rounded-[8px] border border-[var(--border-default)] px-2 text-[12px] outline-none focus:border-[var(--brand-indigo)]"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* last-month match */}
          <div className="mt-3 rounded-[12px] bg-[var(--brand-indigo-tint)] px-3.5 py-3">
            <div className="text-[12.5px] font-bold text-[var(--text-primary)]">
              Last month, this rule would have matched {draft.matchedLastMonth} gaps
            </div>
            <div className="mt-0.5 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">
              {draft.unfilledLastMonth} of them went unfilled. Checked against the schedule — nobody is contacted until you
              save.
            </div>
          </div>
        </div>
      </div>

      <RmFooter
        left={
          !isNew && draft.custom ? (
            <button type="button" onClick={onDelete} className="text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline">
              Delete rule
            </button>
          ) : (
            <span>New rules go to the bottom of the list. Drag them up on the settings page.</span>
          )
        }
      >
        <RmButton onClick={onCancel}>Cancel</RmButton>
        <RmButton kind="primary" onClick={() => { onSave(draft); }}>Save rule</RmButton>
      </RmFooter>
    </RmEditorPage>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{children}</div>;
}

const SAMPLE = [
  { name: 'Leo Martinez', meta: '9 · Ava (RDH)', why: 'Fluoride due · last one 7 months ago', blocked: false },
  { name: 'Amara Okafor', meta: '12 · Ava (RDH)', why: 'Sealants recommended at last exam', blocked: false },
  { name: 'Noah Rezaei', meta: '7 · Jordan (RDH)', why: 'Fluoride due', blocked: false },
  { name: 'Mia Chen', meta: '15 · Ava (RDH)', why: 'Sealants recommended at last exam', blocked: false },
  { name: 'Ethan Brooks', meta: '10 · Jordan (RDH)', why: 'Fluoride due', blocked: true },
];
